import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  QrCode,
  Send,
  CheckCircle,
  Loader2,
  Copy,
  DollarSign,
  CreditCard,
  Banknote,
  ExternalLink,
} from "lucide-react";
import QRCode from "react-qr-code";

const PaymentActions = ({ appointment, api, onUpdate }) => {
  const [showQR, setShowQR] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);

  const remaining =
    appointment.remaining_amount || appointment.service_price || 0;
  const depositPaid = appointment.deposit_amount || 0;
  const paymentStatus = appointment.payment_status || "no_deposit";
  const isFullyPaid = paymentStatus === "fully_paid";

  const fetchPaymentInfo = async () => {
    setLoadingInfo(true);
    try {
      const response = await api.get(
        `/appointments/${appointment.id}/payment-info`,
      );
      setPaymentInfo(response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch payment info:", error);
      toast.error("Failed to load payment info");
      return null;
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleShowQR = async () => {
    const info = await fetchPaymentInfo();
    if (info) {
      setShowQR(true);
    }
  };

  const handleSendLink = async (method = "email") => {
    setSendingLink(true);
    try {
      const response = await api.post(
        `/appointments/${appointment.id}/send-payment-link`,
        {
          method,
        },
      );
      toast.success(response.data.message);

      if (method === "sms" && response.data.sms_text) {
        // Copy SMS text to clipboard
        navigator.clipboard.writeText(response.data.sms_text);
        toast.info("SMS text copied to clipboard");
      }
    } catch (error) {
      console.error("Failed to send payment link:", error);
      toast.error(
        error.response?.data?.detail || "Failed to send payment link",
      );
    } finally {
      setSendingLink(false);
    }
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      await api.post(
        `/appointments/${appointment.id}/mark-paid?payment_method=${paymentMethod}`,
      );
      toast.success("Appointment marked as paid");
      onUpdate?.();
    } catch (error) {
      console.error("Failed to mark as paid:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const copyPaymentLink = () => {
    if (paymentInfo?.payment_url) {
      navigator.clipboard.writeText(paymentInfo.payment_url);
      toast.success("Payment link copied!");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(amount || 0);
  };

  if (isFullyPaid) {
    return (
      <div className="p-4 bg-green-50 rounded-xl border border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Fully Paid</span>
        </div>
        <p className="text-sm text-green-600 mt-1">
          Total: {formatCurrency(appointment.service_price)}
        </p>
      </div>
    );
  }

  if (remaining <= 0) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="payment-actions">
      {/* Payment Status Summary */}
      <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-yellow-700">Balance Due</span>
          <span className="text-xl font-bold text-yellow-700">
            {formatCurrency(remaining)}
          </span>
        </div>
        {depositPaid > 0 && (
          <p className="text-xs text-yellow-600">
            Deposit of {formatCurrency(depositPaid)} already paid
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          onClick={handleShowQR}
          disabled={loadingInfo}
          className="flex items-center gap-2"
          data-testid="show-qr-btn"
        >
          {loadingInfo ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <QrCode className="w-4 h-4" />
          )}
          Show QR
        </Button>

        <Button
          variant="outline"
          onClick={() => handleSendLink("email")}
          disabled={sendingLink}
          className="flex items-center gap-2"
          data-testid="send-link-btn"
        >
          {sendingLink ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Send Link
        </Button>
      </div>

      {/* Mark as Paid Section */}
      <div className="p-4 bg-gray-50 rounded-xl space-y-3">
        <p className="text-sm font-medium text-gray-700">Manual Payment</p>
        <div className="flex gap-2">
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Cash
                </div>
              </SelectItem>
              <SelectItem value="card">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Card (EFTPOS)
                </div>
              </SelectItem>
              <SelectItem value="other">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Other
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={handleMarkPaid}
            disabled={markingPaid}
            className="bg-green-600 hover:bg-green-700"
            data-testid="mark-paid-btn"
          >
            {markingPaid ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          Mark as paid if customer paid via cash or external EFTPOS
        </p>
      </div>

      {/* QR Code Modal */}
      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              Payment QR Code
            </DialogTitle>
          </DialogHeader>

          {paymentInfo && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-6 bg-white rounded-xl border">
                <QRCode value={paymentInfo.payment_url} size={200} level="M" />
              </div>

              {/* Payment Details */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Customer</span>
                  <span className="font-medium">
                    {paymentInfo.customer_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Service</span>
                  <span className="font-medium">
                    {paymentInfo.service_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount Due</span>
                  <span className="font-bold text-purple-600">
                    {formatCurrency(paymentInfo.remaining_amount)}
                  </span>
                </div>
              </div>

              {/* Instructions */}
              <p className="text-center text-sm text-gray-500">
                Customer scans this QR code with their phone to pay
              </p>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={copyPaymentLink}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(paymentInfo.payment_url, "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentActions;
